import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Interactive before/after comparison with a draggable divider (mouse + touch).
 * Optional: aspectRatio ('1 / 1' default), fit ('contain' | 'cover'), testId, alt texts.
 */
export const BeforeAfterSlider = ({
  beforeSrc, afterSrc, language, labels = null, hint = null,
  aspectRatio = '1 / 1', fit = 'contain', testId = 'simulation-comparison-slider',
  beforeAlt = 'Before original', afterAlt = 'After simulation',
}) => {
  const [leftLabel, rightLabel] = labels || (language === 'fr' ? ['AVANT', 'APR\u00c8S'] : ['BEFORE', 'AFTER']);
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const ref = useRef(null);

  const moveTo = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(percent);
  };

  return (
    <>
      <div
        ref={ref}
        className="relative w-full rounded-xl overflow-hidden cursor-col-resize select-none border border-white/10 bg-black"
        style={{ aspectRatio, maxHeight: '600px' }}
        onMouseDown={(e) => { setIsDragging(true); moveTo(e); }}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        onMouseMove={(e) => isDragging && moveTo(e)}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => setIsDragging(false)}
        onTouchMove={moveTo}
        data-testid={testId}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={afterSrc} alt={afterAlt} className={`w-full h-full ${fit === 'cover' ? 'object-cover' : 'object-contain'}`} />
        </div>
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
          <div className="h-full flex items-center justify-center" style={{ width: `${ref.current?.offsetWidth || 800}px` }}>
            <img src={beforeSrc} alt={beforeAlt} className={`w-full h-full ${fit === 'cover' ? 'object-cover' : 'object-contain'}`} />
          </div>
        </div>
        <div className="absolute top-0 bottom-0 w-[3px] bg-[var(--lux-gold)] z-10" style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[var(--lux-gold)] shadow-lg flex items-center justify-center">
            <ChevronLeft className="w-4 h-4 text-[var(--lux-ink)] -mr-1" />
            <ChevronRight className="w-4 h-4 text-[var(--lux-ink)] -ml-1" />
          </div>
        </div>
        <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/70 backdrop-blur-sm text-xs text-white font-medium z-20">
          {leftLabel}
        </div>
        <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-lux-gold/90 backdrop-blur-sm text-xs text-[var(--lux-ink)] font-bold z-20">
          {rightLabel}
        </div>
      </div>
      <p className="text-white/30 text-xs text-center mt-3">
        {hint || (language === 'fr' ? 'Glissez le curseur pour comparer avant et apr\u00e8s' : 'Drag the slider to compare before and after')}
      </p>
    </>
  );
};
